import { useParams, Link } from "react-router-dom";
import { articles, categories } from "@/mocks/sportsData";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类不存在</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
        </div>
      </div>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://www.jisusports.com/" },
      { "@type": "ListItem", position: 2, name: `${category.name}资讯`, item: `https://www.jisusports.com/category/${slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name}赛事资讯 - 极速体育`}
        description={`极速体育${category.name}频道，提供最新${category.name}赛事直播、比分更新、新闻资讯和专业分析。实时掌握${category.name}赛场动态。`}
        keywords={`极速体育${category.name},${category.name}直播,${category.name}比分,${category.name}资讯`}
        canonicalUrl={`https://www.jisusports.com/category/${slug}`}
        structuredData={breadcrumbSchema}
      />

      <main className="pt-20">
        <Breadcrumb items={[{ label: "首页", href: "/" }, { label: `${category.name}资讯` }]} />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}资讯
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  共 {categoryArticles.length} 篇相关文章
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto flex-wrap">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    c.slug === slug
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mb-4 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line text-xs"></i>
                            {article.readCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line text-xs"></i>
                            {article.commentCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-article-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}